import { MenuItem, Paper } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Formik } from 'formik';
import { useState } from 'react';
import { useMutation } from 'react-query';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { LabelledOutline } from '../../common/final-form-material-ui';
import { FormikSelect, FormikTextField } from '../../common/formik-material-ui';
import { user } from '../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { ModalWithStyles } from '../components/modals/ModalWithStyles';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});
const useStyles = makeStyles((theme) => ({
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
}));

/**
 * Parses a dollar string into a numeric value.
 *
 * @param {string} dollarString - The dollar string to parse, e.g., '$6,631.86'. It can be a
 * string representing a monetary value, or a numeric value, in which case it will be returned
 * as is.
 * @returns {number|undefined} The numeric value extracted from the dollar string,
 * or undefined if the input is falsy or cannot be parsed.
 */
const parseDollarString = (dollarString) => {
  if (!dollarString) {
    return;
  }
  if (typeof dollarString === 'number') {
    return dollarString;
  }
  return parseFloat(dollarString.replace(/[^0-9.-]+/g, ''));
};

export function RechargeDetails({ show, onHide }) {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const classes = useStyles();
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  // const makeRequest = useMakePHPRequest({
  //   userAtom: userAtom.contents,
  // });
  // const businessData = useRecoilValueLoadable(
  //   makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_DATA })
  // );
  const mutation = useMutation(getApiMutation(userAtom.contents, setUserAtom));
  const data = {
    call: methodNameEnum.GET_BUSINESS_DATA,
  };
  const businessData = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const setRefetchToggleForBusinessData = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_DATA)
  );

  const intlCurrencyVars = {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  };
  const rechargeAndMinimumValues = [
    25,
    50,
    100,
    500,
    600,
    1000,
    1500,
    2000,
    3000,
    4000,
    5000,
  ];

  if (
    process.env.VITE_APP_INCREASE_BUSINESS_RECHARGE_AND_MIN_BALANCES === '0'
  ) {
    const requiredValues = [10, 15, 25];
    requiredValues.forEach((value) => {
      rechargeAndMinimumValues.push(value);
    });
  }

  return (
    <Paper square={true} elevation={0}>
      <Formik
        onSubmit={async (values, actions) => {
          const data = {
            call: methodNameEnum.UPDATE_RECHARGE,
            apikey: userAtom.contents?.apikey,
            minamt: values.minimumBalance,
            rechargeamt: values.recharge,
          };
          const response = await mutation.mutateAsync({
            endpoint: endpointEnum.UPDATE_RECHARGE,
            payload: data,
          });
          if (response?.data?.success) {
            enqueueSnackbarWithStyles({
              message: 'Successfully Updated Recharge Settings',
              alertProps: { severity: 'success' },
            });
          } else {
            enqueueSnackbarWithStyles({
              message:
                'Encountered an error while trying to update recharge settings',
              alertProps: { severity: 'warning' },
            });
          }
          setRefetchToggleForBusinessData({});
          onHide();
        }}
        enableReinitialize={true}
        // validationSchema={changeTeamNameSchema}
        initialValues={{
          currentBalance: businessData?.contents?.response?.data
            ? formatter.format(
                businessData?.contents?.response?.data?.result?.[0]
                  ?.reserveBalance
              )
            : 0,
          minutesBalance: businessData?.contents?.response?.data
            ? +businessData?.contents?.response?.data?.result?.[0]
                ?.minutesBalance
            : 0,
          recharge: businessData?.contents?.response?.data
            ? +businessData?.contents?.response?.data?.result?.[0]
                ?.rechargeAmount
            : process.env
                .VITE_APP_INCREASE_BUSINESS_RECHARGE_AND_MIN_BALANCES === '0'
            ? 10
            : 50,
          minimumBalance: businessData?.contents?.response?.data
            ? +businessData?.contents?.response?.data?.result?.[0]
                ?.minimumBalance
            : process.env.VITE_INCREASE_BUSINESS_RECHARGE_AND_MIN_BALANCES ===
              '0'
            ? 10
            : 50,
          // currentBalance: businessData.contents?.data
          //   ? formatter.format(
          //       businessData.contents?.data?.result?.reserveBalance
          //     )
          //   : 0,
          // minutesBalance: businessData.contents?.data
          //   ? +businessData.contents?.data?.result?.minutesBalance
          //   : 0,
          // recharge: businessData.contents?.data
          //   ? +businessData.contents?.data?.result?.rechargeAmount
          //   : 10,
          // minimumBalance: businessData.contents?.data
          //   ? +businessData.contents?.data?.result?.minimumBalance
          //   : 10,
        }}
      >
        {(formik) => {
          const newMinLargerThanBalance =
            parseDollarString(formik.values.minimumBalance) >
            parseDollarString(formik.values.currentBalance);
          return (
            <>
              <ModalWithStyles
                onSubmit={() => {
                  newMinLargerThanBalance
                    ? () => setShowConfirmModal(true)
                    : formik.handleSubmit();
                }}
                title="Minutes/SMS Reserve"
                size="xs"
                aria-labelledby="example-modal-sizes-title-lg"
                show={show}
                onHide={() => {
                  onHide();
                }}
                isSubmitting={formik.isSubmitting}
                isLoading={formik.isSubmitting}
                disableSubmit={!formik.dirty}
              >
                <FormikTextField
                  name="currentBalance"
                  className={classes.field}
                  formik={formik}
                  fullWidth
                  label="Current Balance"
                  InputProps={{
                    readOnly: true,
                  }}
                  size="small"
                  variant="outlined"
                />
                <FormikTextField
                  name="minutesBalance"
                  className={classes.field}
                  formik={formik}
                  fullWidth
                  label="Minutes Available"
                  InputProps={{
                    readOnly: true,
                  }}
                  size="small"
                  variant="outlined"
                />
                <LabelledOutline label="Recharge Configuration">
                  <FormikSelect
                    fullWidth
                    name="recharge"
                    className={classes.field}
                    required
                    formik={formik}
                    label="Automatically Recharge For"
                    size="small"
                    variant="outlined"
                  >
                    {rechargeAndMinimumValues
                      .sort(function (a, b) {
                        return a - b;
                      })
                      .map((value) => {
                        return (
                          <MenuItem value={value} key={value}>
                            {new Intl.NumberFormat(
                              'en-US',
                              intlCurrencyVars
                            ).format(value)}
                          </MenuItem>
                        );
                      })}
                  </FormikSelect>
                  <FormikSelect
                    fullWidth
                    name="minimumBalance"
                    required
                    className={classes.field}
                    formik={formik}
                    label="When Your Balance Falls Below "
                    size="small"
                    variant="outlined"
                  >
                    {rechargeAndMinimumValues
                      .sort(function (a, b) {
                        return a - b;
                      })
                      .map((value) => {
                        return (
                          <MenuItem value={value} key={value}>
                            {new Intl.NumberFormat(
                              'en-US',
                              intlCurrencyVars
                            ).format(value)}
                          </MenuItem>
                        );
                      })}
                  </FormikSelect>
                </LabelledOutline>
                <br />
              </ModalWithStyles>
              <ModalWithStyles
                onSubmit={formik.handleSubmit}
                title="Confirm Reserve Balance"
                aria-labelledby="example-modal-sizes-title-md"
                show={showConfirmModal}
                onHide={() => setShowConfirmModal(false)}
                isSubmitting={formik.isSubmitting}
                isLoading={formik.isSubmitting}
                size="xs"
              >
                <p>
                  {`You are about to set your Minimum Reserve Balance to $${formik.values.minimumBalance}.`}
                </p>
                <p>
                  {`Your Minutes Reserve Balance will be replenished instantly for
                  the amount of $${parseDollarString(
                    formik.values.recharge
                  ).toFixed(2)}.`}
                </p>
                <p>Are you sure you want to continue?</p>
              </ModalWithStyles>
            </>
          );
        }}
      </Formik>
    </Paper>
  );
}
